import { useMemo, useState } from 'react'
import { useData } from '@/contexts/data'
import { FilterOption } from '@/types/common'

export type { FiltersState } from '@/types/common';

export function useFilter() {
  const {
    territories,
    territoriesLoading,
    contexts,
    contextsLoading,
    topics,
    topicsLoading,
    partners,
    partnersLoading,
    sources,
    sourcesLoading,
  } = useData()
  const [favorited, setFavorited] = useState<boolean | undefined>(undefined)
  const [createdBy, setCreatedBy] = useState<'AGENT' | 'PLATFORM' | ''>('')
  const [ids, setIds] = useState({
    territories: [] as string[],
    contexts: [] as string[],
    topics: [] as string[],
    partners: [] as string[],
    sources: [] as string[],
  })

  const territoryOptions: FilterOption[] = useMemo(() => territories.map(t => ({ id: t.id, name: t.name })), [territories])
  const contextOptions: FilterOption[] = useMemo(() => contexts.map(c => ({ id: c.id, name: c.name })), [contexts])
  const topicOptions: FilterOption[] = useMemo(() => topics.map(t => ({ id: t.id, name: t.name })), [topics])
  const partnerOptions: FilterOption[] = useMemo(() => partners.map(p => ({ id: p.id, name: p.name })), [partners])
  const sourceOptions: FilterOption[] = useMemo(() => sources.map(s => ({ id: s.id, name: s.name })), [sources])

  const clear = () => {
    setIds({ territories: [], contexts: [], topics: [], partners: [], sources: [] })
    setFavorited(undefined)
    setCreatedBy('')
  }

  type SettableKey =
    | 'territoriesIds'
    | 'contextsIds'
    | 'topicsIds'
    | 'partnersIds'
    | 'sourcesIds'
    | 'favorited'
    | 'createdBy'

  const set = (key: SettableKey, value: any) => {
    switch (key) {
      case 'territoriesIds':
        setIds(prev => ({ ...prev, territories: value as string[] }));
        break
      case 'contextsIds':
        setIds(prev => ({ ...prev, contexts: value as string[] }));
        break
      case 'topicsIds':
        setIds(prev => ({ ...prev, topics: value as string[] }));
        break
      case 'partnersIds':
        setIds(prev => ({ ...prev, partners: value as string[] }));
        break
      case 'sourcesIds':
        setIds(prev => ({ ...prev, sources: value as string[] }));
        break
      case 'favorited':
        setFavorited(value as boolean | undefined)
        break
      case 'createdBy':
        setCreatedBy(value as 'AGENT' | 'PLATFORM' | '')
        break
      default:
        break
    }
  }

  const data = {
    territoriesIds: useMemo(() => ids.territories, [ids]),
    contextsIds: useMemo(() => ids.contexts, [ids]),
    topicsIds: useMemo(() => ids.topics, [ids]),
    partnersIds: useMemo(() => ids.partners, [ids]),
    sourcesIds: useMemo(() => ids.sources, [ids]),
    favorited,
    createdBy,
    territories: territoryOptions,
    contexts: contextOptions,
    topics: topicOptions,
    partners: partnerOptions,
    sources: sourceOptions,
    isLoadingTerritories: territoriesLoading,
    isLoadingContexts: contextsLoading,
    isLoadingTopics: topicsLoading,
    isLoadingPartners: partnersLoading,
    isLoadingSources: sourcesLoading,
  }

  return { data, set, clear }
}

